import type { PcaField } from "@/components/BatchCall/pca-filter-types";
import type { Agent } from "@/services/assistant.service";

/** One call's post-call-analysis answers, as call_logs.analysis_data holds them. */
export type AnalysisData = Record<string, unknown> | null | undefined;

/** The fields the agent's post-call analysis asks for; none while it is switched off. */
export const agentPcaFields = (agent: Agent): PcaField[] => {
  const pca = agent.config?.settings?.postCallAnalysis;
  return pca?.enabled && Array.isArray(pca.fields) ? pca.fields : [];
};

/** A key's type, read off its answers: all booleans, all numbers, else text to search. */
const typeOf = (answers: unknown[]): PcaField["type"] => {
  const given = answers.filter((answer) => answer !== null && answer !== undefined);
  if (given.length === 0) return "string";
  if (given.every((answer) => typeof answer === "boolean")) return "boolean";
  if (given.every((answer) => typeof answer === "number")) return "number";
  return "string";
};

/**
 * `known`, followed by a field for every other key the calls' analysis_data
 * holds — first-seen order, each typed by its answers. The answers outlive the
 * agent, so this is where a campaign whose agent was deleted gets its filters,
 * and what every export appends so that no answer is dropped.
 *
 * Returns `known` itself when there is nothing new, so it can be kept in state.
 */
export const withFoundPcaFields = (known: PcaField[], calls: AnalysisData[]): PcaField[] => {
  const answers = new Map<string, unknown[]>();
  for (const data of calls) {
    for (const [name, answer] of Object.entries(data ?? {})) {
      if (known.some((field) => field.name === name)) continue;
      const seen = answers.get(name);
      if (seen) seen.push(answer);
      else answers.set(name, [answer]);
    }
  }
  if (answers.size === 0) return known;
  return [...known, ...Array.from(answers, ([name, values]) => ({ name, type: typeOf(values) }))];
};

/**
 * The analysis filters a campaign offers. `agent` is its agent as `linkedRow`
 * reads it: undefined while there is no copy of it (no filters yet), null when
 * the campaign has none or it was deleted (the fields `found` in its calls),
 * else the agent's own fields.
 */
export const campaignFilterFields = (agent: Agent | null | undefined, found: PcaField[]): PcaField[] =>
  agent === null ? found : agent ? agentPcaFields(agent) : [];

/** The analysis columns of a campaign export: the agent's fields, then every other key in the rows. */
export const campaignExportFields = (agent: Agent | null | undefined, calls: AnalysisData[]): PcaField[] =>
  withFoundPcaFields(agent ? agentPcaFields(agent) : [], calls);
